import React, { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../apiService';
import { signInWithGoogle, signInWithMicrosoft, signOutFromFirebase, isSSOConfigured, isMicrosoftSSOEnabled } from '../../firebaseSSO';
import { startKeycloakLogin } from '../../auth/oidc';
import RequestAccessModal from './RequestAccessModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import { PRIVACY_POLICY_URL, SIGNUP_CONSENT_VERSION, TERMS_URL } from './signupConsent';
import { SIGNED_OUT_OF_BARK_NOTICE, signInRefusalMessage } from './signInErrors';

/** BAR-1564: who signs people in. `pending` while config.json is still loading. */
export type LoginAuthMode = 'firebase' | 'keycloak' | 'pending';

/** Keycloak's "Forgot password?" link opens the app with this query flag. */
const FORGOT_PASSWORD_PARAM = 'forgot_password';

/** Keycloak's "New user? Request access" link opens the app with this query flag. */
const REQUEST_ACCESS_PARAM = 'request_access';

const currentPath = (): string => (
    typeof window === 'undefined'
        ? '/'
        : window.location.pathname + window.location.search + window.location.hash
);

// BAR-1564: a status view (logo, spinner, at most one line of status) is never
// a dead end: after this long it gives way to the page as it would otherwise show.
const STATUS_VIEW_MAX_MS = 10000;

/** BAR-1564: what a status view is waiting for. */
type StatusKind = 'redirecting' | 'pending' | 'signOut' | 'restore';

interface LoginPageProps {
    onLoginSuccess: (token: string, userData?: any, refreshToken?: string) => void;
    /**
     * Offer a free account to someone who has none: Google and Microsoft then
     * sign in OR sign up, under the signup consent shown below the buttons.
     * RequireAuth sets it while an AI assistant's connector sign-in waits, so
     * a person sent here by Claude can create the account where they are asked
     * to sign in, and go straight on to approving the connection.
     */
    offerSignup?: boolean;
    /**
     * BAR-1564: `keycloak` replaces the password form with Keycloak's hosted
     * sign-in page (the Google and Microsoft buttons go there with
     * `kc_idp_hint`). `pending` shows neither until config.json says which.
     * Default `firebase`: the page as before Keycloak.
     */
    authMode?: LoginAuthMode;
    /** Keycloak mode: where to return after sign-in. Default: the current URL. */
    returnTo?: string;
    /** A message from the last sign-in attempt (a refused Keycloak sign-in). */
    notice?: string | null;
    /**
     * BAR-1564: how the notice reads — 'info' (a failed sign-out is news, not
     * a fault) shows it in neutral text instead of danger. Only while the
     * notice is the message shown; any other message stays danger.
     */
    noticeTone?: 'error' | 'info';
    /**
     * BAR-1564: keycloak mode only (firebase and pending ignore it) — what the
     * app is still doing before this page may show its sign-in or go to
     * Keycloak: 'restore' for the startup restore, 'signOut' for a sign-out.
     * While set (and nothing else is on the page) only the BARK logo and a
     * spinner show.
     */
    waitingFor?: 'restore' | 'signOut' | null;
    /** Keycloak mode: ask for credentials even if Keycloak still has a session. */
    forceLoginPrompt?: boolean;
    /**
     * BAR-1564: called once when the sign-out view runs out while Keycloak's
     * sign-out is still on its way, so the app asks for credentials at the
     * next sign-in in this browser, after a reload or in a new tab too.
     */
    onSignOutLate?: () => void;
    /**
     * BAR-1564: keycloak mode only — let this page send the browser to
     * Keycloak's hosted sign-in page by itself, so there is one sign-in page,
     * not two. RequireAuth sets it only when nothing here needs the person
     * first; the fallback page below is what shows when it must not (or could
     * not) redirect. Firebase and `pending` modes ignore it.
     */
    autoRedirect?: boolean;
}

function LoginPage({
    onLoginSuccess,
    offerSignup = false,
    authMode = 'firebase',
    returnTo,
    notice = null,
    noticeTone = 'error',
    waitingFor = null,
    forceLoginPrompt = false,
    onSignOutLate,
    autoRedirect = false,
}: LoginPageProps) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSSOLoading, setIsSSOLoading] = useState<string | null>(null);
    const [isSSOEnabled, setIsSSOEnabled] = useState(false);
    const [microsoftEnabled, setMicrosoftEnabled] = useState(false);
    const [message, setMessage] = useState(notice ?? '');
    // Keycloak's "New user? Request access" sends people to `/?request_access=1`.
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(() => (
        typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get(REQUEST_ACCESS_PARAM) === '1'
    ));
    // Keycloak's "Forgot password?" sends people to `/?forgot_password=1`.
    const [isForgotModalOpen, setIsForgotModalOpen] = useState(() => (
        typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get(FORGOT_PASSWORD_PARAM) === '1'
    ));
    const [keycloakRedirect, setKeycloakRedirect] = useState<'password' | 'google' | 'microsoft' | null>(null);
    // BAR-1564: the automatic redirect runs at most once per mount; the ref is
    // the guard (safe under React StrictMode's double effects), the phase drives
    // the "Redirecting…" render. 'stopped' once the redirect must not retry by
    // itself: it failed, the browser came back through the bfcache, or the
    // person clicked Sign in (a manual sign-in shows its own busy button).
    const autoRedirectStartedRef = useRef(false);
    const [autoRedirectPhase, setAutoRedirectPhase] = useState<'idle' | 'redirecting' | 'stopped'>('idle');
    const isKeycloak = authMode === 'keycloak';

    // BAR-1564: a status view is decided here, during render, so the first
    // painted frame is already right. "Nothing on the page" = no message, both
    // dialogs closed and no manual sign-in under way (its busy button says the
    // browser is leaving; a click that clears a notice must not bring back the
    // sign-out or restore view). Priority: the automatic redirect, a pending
    // config, a sign-out under way, the startup restore.
    const nothingOnPage = !message && !isRequestModalOpen && !isForgotModalOpen && keycloakRedirect === null;
    const statusKind: StatusKind | null =
        !nothingOnPage ? null
        : isKeycloak && autoRedirect && autoRedirectPhase !== 'stopped' ? 'redirecting'
        : authMode === 'pending' ? 'pending'
        : isKeycloak && waitingFor === 'signOut' ? 'signOut'
        : isKeycloak && waitingFor === 'restore' ? 'restore'
        : null;
    // BAR-1564: never a dead end — a status view gives way to the page after
    // STATUS_VIEW_MAX_MS. Each kind gets its own 10 s (a change of kind re-runs
    // the effect and starts a new one), and a kind that ran out stays out,
    // whatever came between.
    const [expiredStatusKinds, setExpiredStatusKinds] = useState<ReadonlySet<StatusKind>>(() => new Set());
    useEffect(() => {
        if (!statusKind) return;
        const timer = setTimeout(() => setExpiredStatusKinds(prev => (
            prev.has(statusKind) ? prev : new Set(prev).add(statusKind)
        )), STATUS_VIEW_MAX_MS);
        return () => clearTimeout(timer);
    }, [statusKind]);
    // The settings (config.json) did not arrive within the status view's 10 s:
    // there is no way to sign in until they do, so the page offers a reload.
    const settingsLate = authMode === 'pending' && expiredStatusKinds.has('pending');
    // The sign-out view ran out while Keycloak's sign-out is still on its way.
    // BARK's session is gone; Keycloak's may not be yet. The page says so, as
    // a failed sign-out does, and a sign-in from here asks for credentials: a
    // click on Sign in cancels that sign-out and must not reuse its session.
    const signOutLate = isKeycloak && waitingFor === 'signOut' && expiredStatusKinds.has('signOut');
    // That holds beyond this page: Keycloak may still have the session when
    // the browser comes back or another tab opens, so the app keeps it.
    useEffect(() => {
        if (signOutLate) onSignOutLate?.();
    }, [signOutLate, onSignOutLate]);

    useEffect(() => {
        isSSOConfigured().then(setIsSSOEnabled);
        isMicrosoftSSOEnabled().then(setMicrosoftEnabled);
    }, []);

    // A new notice replaces the message; a notice going back to null leaves it.
    // Today the only clear is App's successful Keycloak callback, followed
    // directly by handleLoginSuccess, which unmounts this page. A clear added
    // while this page stays mounted must clear the message too, or it stays on
    // screen and, as the automatic redirect's stop condition, keeps the
    // redirect off. Not `setMessage(notice ?? '')`: that would also wipe a
    // message set on this page.
    useEffect(() => {
        if (notice) setMessage(notice);
    }, [notice]);

    // Back from Keycloak through the back/forward cache, the page returns as it
    // was left, mid-redirect: give the buttons back, and stop the automatic
    // redirect for this mount — the person chose to come back.
    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                setKeycloakRedirect(null);
                setAutoRedirectPhase('stopped');
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    // Strip the flags once read, so a refresh does not reopen the dialogs.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        if (!params.has(FORGOT_PASSWORD_PARAM) && !params.has(REQUEST_ACCESS_PARAM)) return;
        params.delete(FORGOT_PASSWORD_PARAM);
        params.delete(REQUEST_ACCESS_PARAM);
        const remaining = params.toString();
        window.history.replaceState(window.history.state, '', window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash);
    }, []);

    // BAR-1564: one sign-in page. When nothing here needs the person (no
    // message, no dialog), Keycloak's page IS the sign-in page, so go there at
    // once — with `replace`, so Back does not land on a page that only forwards
    // again. Closing a dialog re-runs this effect and lets the redirect go.
    useEffect(() => {
        if (!isKeycloak || !autoRedirect) return;
        if (message || isRequestModalOpen || isForgotModalOpen) return;
        if (autoRedirectStartedRef.current) return;
        autoRedirectStartedRef.current = true;
        setAutoRedirectPhase('redirecting');
        startKeycloakLogin({
            returnTo: returnTo ?? currentPath(),
            forceLogin: forceLoginPrompt,
            replace: true,
        }).catch((error: unknown) => {
            console.error('[Auth] Could not start the Keycloak sign-in:', error);
            setMessage('Sign-in is unavailable right now. Please try again.');
            // No retry loop: the fallback page's Sign in button is the retry.
            setAutoRedirectPhase('stopped');
        });
    }, [isKeycloak, autoRedirect, message, isRequestModalOpen, isForgotModalOpen, returnTo, forceLoginPrompt]);

    // BAR-1564: the browser leaves for Keycloak; it returns to /auth/callback.
    const handleKeycloakLogin = async (idpHint?: 'google' | 'microsoft') => {
        // A manual sign-in is this mount's one redirect: clearing the message
        // below must not let the automatic effect fire a second one, and the
        // busy button (not the status view) says the browser is leaving.
        autoRedirectStartedRef.current = true;
        setAutoRedirectPhase('stopped');
        setKeycloakRedirect(idpHint ?? 'password');
        setMessage('');
        try {
            await startKeycloakLogin({
                returnTo: returnTo ?? currentPath(),
                idpHint,
                // As with Firebase: only Google and Microsoft create a free account.
                signupConsentVersion: offerSignup && idpHint ? SIGNUP_CONSENT_VERSION : undefined,
                forceLogin: forceLoginPrompt || signOutLate,
            });
        } catch (error: unknown) {
            console.error('[Auth] Could not start the Keycloak sign-in:', error);
            setMessage('Sign-in is unavailable right now. Please try again.');
            setKeycloakRedirect(null);
        }
    };

    // BAR-1564: Keycloak's own sign-in page has the Google and Microsoft buttons
    // (it brokers them, so no Firebase keys are needed). In keycloak mode this
    // page is only a fallback and shows them only while a connector sign-up
    // waits for the consent shown here (offerSignup). Firebase mode shows them
    // where Firebase SSO is configured.
    const showSSO = isKeycloak ? offerSignup : (authMode === 'firebase' && isSSOEnabled);
    const ssoBusy = isKeycloak ? keycloakRedirect !== null : isSSOLoading !== null || isLoading;
    const ssoLabel = (provider: 'google' | 'microsoft', name: string): string => {
        const busy = isKeycloak ? keycloakRedirect === provider : isSSOLoading === provider;
        if (busy) return 'Signing in...';
        return offerSignup ? `Continue with ${name}` : `Sign in with ${name}`;
    };
    const handleSSOClick = (provider: 'google' | 'microsoft') => (
        isKeycloak ? handleKeycloakLogin(provider) : handleSSOLogin(provider)
    );

    const handleSSOLogin = async (provider: 'google' | 'microsoft') => {
        setIsSSOLoading(provider);
        setMessage('');
        try {
            const { idToken, refreshToken } = provider === 'google'
                ? await signInWithGoogle()
                : await signInWithMicrosoft();
            const result = offerSignup
                ? await api.loginWithSSOToken(idToken, refreshToken, SIGNUP_CONSENT_VERSION)
                : await api.loginWithSSOToken(idToken, refreshToken);
            // Backend exchange succeeded — only NOW sign out of the Firebase JS SDK's
            // local session. If we'd signed out before the exchange and it failed
            // (5xx, network drop), the retry path would have had to reopen the popup
            // instead of reusing the still-valid Firebase session.
            await signOutFromFirebase();
            onLoginSuccess(result.access_token, result.user, result.refresh_token);
        } catch (error: unknown) {
            const raw = error instanceof Error ? error.message : 'SSO login failed';
            // Firebase popup-closed-by-user is not an error worth surfacing
            if (raw.includes('popup-closed-by-user') || raw.includes('cancelled-popup-request')) {
                setMessage('');
                return;
            }
            setMessage(signInRefusalMessage(error, { sso: true }));
        } finally {
            setIsSSOLoading(null);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        
        try {
            const result = await api.login(email, password);
            onLoginSuccess(result.access_token, result.user, result.refresh_token);
            setMessage("Login successful!");
        } catch (error: any) {
            setMessage(error.message || "Login failed");
        } finally {
            setIsLoading(false);
        }
    };

    // BAR-1564: while the app is busy (or the browser is on its way to
    // Keycloak) there is nothing to do here: the logo, the standard spinner
    // and at most a word about what is happening. Starting up and restoring a
    // session say "Loading…" to screen readers only, so no words come and go
    // on the way to Keycloak. The line under the spinner keeps its height
    // when empty, so nothing moves when words appear.
    if (statusKind && !expiredStatusKinds.has(statusKind)) {
        const statusText = statusKind === 'redirecting' ? 'Redirecting to sign-in…'
            : statusKind === 'signOut' ? 'Signing out…'
            : null;
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-surface py-12 px-4 font-sans">
                <div className="flex justify-center items-center mb-6">
                    <img
                        className="h-40 object-contain"
                        src="/Data/images/BARK_tree_2_text.png"
                        alt="BARK Logo"
                    />
                </div>
                <div role="status" className="flex flex-col items-center gap-3 text-sm text-fg-secondary">
                    <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} aria-hidden="true" />
                    <p className="h-5">{statusText ?? <span className="sr-only">Loading…</span>}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex min-h-screen flex-col items-center justify-center bg-surface py-12 px-4 font-sans">
                <div className="w-full max-w-md space-y-8">
                    <div>
                         <div className="flex justify-center items-center mb-6">
                            <img 
                                className="h-40 object-contain"
                                src="/Data/images/BARK_tree_2_text.png"
                                alt="BARK Logo"
                            />
                        </div>
                        <h2 className="mt-4 text-center text-xl tracking-tight text-fg">
                            {settingsLate ? 'Sign-in is taking longer than usual'
                                : offerSignup && showSSO ? 'Sign in, or create your free account'
                                : 'Sign in to your account'}
                        </h2>
                    </div>
                    {message && (
                        // BAR-1564: an 'info' notice (a failed sign-out) is news,
                        // not a fault; any other message stays danger.
                        <p className={`text-center text-sm mb-4 ${message === notice && noticeTone === 'info' ? 'text-fg-secondary' : 'text-danger'}`}>{message}</p>
                    )}
                    {!message && signOutLate && (
                        <p className="text-center text-sm mb-4 text-fg-secondary">{SIGNED_OUT_OF_BARK_NOTICE}</p>
                    )}
                    {isKeycloak && (
                        // Keycloak hosts the password form: this button only goes there.
                        <button
                            type="button"
                            onClick={() => handleKeycloakLogin()}
                            disabled={keycloakRedirect !== null}
                            className="group relative flex w-full justify-center rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-white hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-border-default focus:ring-offset-2 disabled:bg-fg-tertiary transition-colors"
                        >
                            {keycloakRedirect === 'password' ? 'Signing in...' : 'Sign in'}
                        </button>
                    )}
                    {settingsLate && (
                        // The heading says sign-in is late; a reload fetches
                        // the settings again.
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="group relative flex w-full justify-center rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-white hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-border-default focus:ring-offset-2 disabled:bg-fg-tertiary transition-colors"
                        >
                            Try again
                        </button>
                    )}
                    {authMode === 'firebase' && (
                        <form onSubmit={handleLogin} className="space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="email" className="sr-only">Email</label>
                                    <input 
                                        id="email"
                                        value={email} 
                                        onChange={e => setEmail(e.target.value)} 
                                        name="email" 
                                        type="email" 
                                        autoComplete="email"
                                        required 
                                        className="block w-full appearance-none rounded-md border border-border-default px-3 py-3 text-fg placeholder-fg-tertiary focus:z-10 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:text-sm" 
                                        placeholder="Email address" 
                                    />
                                </div>
                                <div>
                                    <label htmlFor="password" className="sr-only">Password</label>
                                    <input 
                                        id="password"
                                        value={password} 
                                        onChange={e => setPassword(e.target.value)} 
                                        name="password" 
                                        type="password" 
                                        autoComplete="current-password"
                                        required 
                                        className="block w-full appearance-none rounded-md border border-border-default px-3 py-3 text-fg placeholder-fg-tertiary focus:z-10 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:text-sm" 
                                        placeholder="Password" 
                                    />
                                </div>
                            </div>
                            <button 
                                type="submit" 
                                disabled={isLoading} 
                                className="group relative flex w-full justify-center rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-white hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-border-default focus:ring-offset-2 disabled:bg-fg-tertiary transition-colors"
                            >
                                {isLoading ? 'Signing in...' : 'Sign in'}
                            </button>
                        </form>
                    )}

                    {showSSO && (
                        <>
                        <div className="relative my-2">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-200" />
                            </div>
                            <div className="relative flex justify-center text-xs">
                                <span className="bg-white px-2 text-slate-400">or continue with</span>
                            </div>
                        </div>

                        <div className="flex flex-col space-y-2">
                            <button
                                type="button"
                                onClick={() => handleSSOClick('google')}
                                disabled={ssoBusy}
                                className="flex w-full items-center justify-center gap-3 rounded-md border border-slate-300 bg-white py-2.5 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                            >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                {ssoLabel('google', 'Google')}
                            </button>

                            {microsoftEnabled && (
                                <button
                                    type="button"
                                    onClick={() => handleSSOClick('microsoft')}
                                    disabled={ssoBusy}
                                    className="flex w-full items-center justify-center gap-3 rounded-md border border-slate-300 bg-white py-2.5 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                                >
                                    <svg className="h-4 w-4" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                                        <path fill="#f35325" d="M1 1h10v10H1z"/>
                                        <path fill="#81bc06" d="M12 1h10v10H12z"/>
                                        <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                                        <path fill="#ffba08" d="M12 12h10v10H12z"/>
                                    </svg>
                                    {ssoLabel('microsoft', 'Microsoft')}
                                </button>
                            )}
                        </div>
                        {offerSignup && (
                            <div className="space-y-2 text-xs leading-relaxed text-fg-secondary" data-testid="signup-consent">
                                <p className="font-medium text-fg">
                                    New to BARK? Continue with {microsoftEnabled ? 'Google or Microsoft' : 'Google'} and your free account is created.
                                </p>
                                {/* The website's consent wording for SIGNUP_CONSENT_VERSION,
                                    word for word: see signupConsent.ts. */}
                                <p>
                                    By creating an account you agree to our{' '}
                                    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-fg">General Terms</a>{' '}
                                    and acknowledge our{' '}
                                    <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-fg">Privacy Policy</a>,
                                    and you confirm that you are registering for your work and that the details you give are correct.
                                    We use your details to set up your access and to contact you about BARK. You can ask us to stop at
                                    any time, and every sales or marketing email carries an unsubscribe link.
                                </p>
                            </div>
                        )}
                        </>
                    )}

                    {/* BAR-1564: not while the settings are late. Both dialogs submit
                        through the API client, which needs the same settings first:
                        Try again is the only action. */}
                    {!settingsLate && (
                        <div className="flex flex-col space-y-3 text-sm mt-4 px-1">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={() => setIsForgotModalOpen(true)}
                                    className="text-fg-secondary hover:text-fg transition-colors"
                                >
                                    Forgot password?
                                </button>
                                <button
                                    onClick={() => setIsRequestModalOpen(true)}
                                    className="font-medium text-fg-secondary hover:text-fg transition-colors"
                                >
                                    Request access
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {isRequestModalOpen && <RequestAccessModal onClose={() => setIsRequestModalOpen(false)} />}
            {isForgotModalOpen && <ForgotPasswordModal onClose={() => setIsForgotModalOpen(false)} />}
        </>
    );
}

export default LoginPage;
